import Component from '@glimmer/component'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { t } from 'ember-intl'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import { SpellingRuleWordsTable } from 'teacher-ui-client/components/writing-legends/tables/spelling-rule-words-table'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type Student from 'teacher-ui-client/models/student'
import { deserialize as deserializeSort } from '@blakeelearning/data-tables/utils/sorting'
import NoReportValue from 'teacher-ui-client/components/reporting/no-report-value'
import ContentTile from 'teacher-ui-client/components/tiles/content'
import BreadcrumbLinkTo from 'teacher-ui-client/components/base/breadcrumbs/link-to'
import { hash } from '@ember/helper'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { toRequestScope, type BaseReport, type BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import { Product } from 'teacher-ui-client/utils/products'
import { array } from '@ember/helper'

export interface SpellingRuleWordsData extends BaseReport {
  issue_words: [
    {
      correct_word: string
      error_rate: number
      incorrect_words: string
      total_correct: number
      total_incorrect: number
    },
  ]
  meta: BaseReportMetaPaging
}

interface Signature {
  Args: {
    uiScope: ModelFor<ReportingUiScopeRoute>['uiScope']
    period: ModelFor<ReportingUiScopeRoute>['period']
    students: Student[]
    pageNumber: number
    perPage: number
  }
}

export class SpellingRuleWords extends Component<Signature> {
  @queryParam('reporting/ui-scope/writing-legends/spelling') sort!: string

  @queryParam('reporting/ui-scope/writing-legends/spelling') rule!: string | undefined

  @service reporting!: ReportingService

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  fetchData = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.uiScope)

    const url = this.reporting.buildGravityUrl({
      product: Product.WL,
      requestScope,
      reportName: 'spelling/issue-words',
    })

    const query = this.reporting.buildQuery({
      sort: this.sort,
      period: this.args.period,
    })

    query.rule = this.rule

    return this.reporting.fetchReport<SpellingRuleWordsData>(url, query)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    {{#if this.latestData}}
      <div>
        <div class="py-2">
          <BreadcrumbLinkTo @route="reporting.ui-scope.writing-legends.spelling" @query={{hash rule=""}}>
            {{t "components.reporting.tables.writingLegends.spelling.spellingRule"}}
          </BreadcrumbLinkTo>

        </div>

        <h2 class="py-3 text-center">{{t "components.reporting.tables.writingLegends.spelling.spellingRule"}}</h2>
      </div>
      <ContentTile>
        <SpellingRuleWordsTable
          class="w-full"
          @isReloading={{this.fetchData.isPending}}
          @spellingRuleWords={{this.latestData}}
        />
      </ContentTile>
    {{else}}
      <NoReportValue @reportStates={{array this.fetchData}} />
    {{/if}}
  </template>
}
